import { useCallback, useEffect, useState } from 'react';
import { callApi } from '../api';
import { Section, Stat, ErrorBox, LoadingSpinner, EmptyState } from '../components/Section';
import { UserChip } from '../components/UserChip';
import { usePolling } from '../hooks/usePolling';
import { useSession } from '../hooks/useSession';
import { statusColor, timeAgo, timeUntil } from '../utils';

const TABS = ['beli', 'google'];

export function Imports() {
  const [tab, setTab] = useState('beli');
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-stone-800">Imports</h1>
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`text-xs px-3 py-1 rounded capitalize ${
                tab === t
                  ? 'bg-stone-800 text-white'
                  : 'bg-white text-stone-700 border'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      {tab === 'beli' && <BeliQueueSection />}
      {tab === 'google' && <GoogleHistorySection />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Beli queue (the actionable side)
// ---------------------------------------------------------------------------
function jobOutcomeText(job) {
  if (job.status === 'failed') return job.error_message || 'Something went wrong.';
  if (job.status === 'succeeded' || job.status === 'partial') {
    const reviews = job.been_reviews_created ?? 0;
    const added = job.bookmarks_added_to_playlist ?? 0;
    if (reviews === 0 && added === 0) return 'Nothing new to import.';
    const parts = [];
    if (reviews > 0) parts.push(`${reviews} ${reviews === 1 ? 'review' : 'reviews'}`);
    if (added > 0) parts.push(`${added} to Want to Go`);
    return parts.join(' • ');
  }
  return null;
}

function JobRow({ job, onRetry, retrying }) {
  const outcome = jobOutcomeText(job);
  const isRetrying = retrying === job.job_id;
  return (
    <div className="flex items-start justify-between py-2 border-b last:border-b-0 gap-3">
      <div className="min-w-0">
        <div className="font-medium text-stone-800 truncate">
          {job.palate_username || job.palate_user_id.slice(0, 8)}
        </div>
        <div className="text-xs text-stone-500 truncate">{job.share_url}</div>
        {outcome && (
          <div className={`text-xs mt-0.5 ${job.status === 'failed' ? 'text-red-600' : 'text-stone-600'}`}>
            {outcome}
          </div>
        )}
        {job.status === 'failed' && onRetry && (
          <button
            onClick={() => onRetry(job.job_id)}
            disabled={isRetrying}
            className="text-xs mt-1 text-stone-700 underline disabled:opacity-50"
          >
            {isRetrying ? 'Retrying…' : 'Retry'}
          </button>
        )}
      </div>
      <div className="text-right text-xs ml-3 whitespace-nowrap">
        <div className={`font-medium ${statusColor(job.status)}`}>{job.status}</div>
        <div className="text-stone-500">
          {timeAgo(job.completed_at || job.claimed_at || job.created_at)}
        </div>
      </div>
    </div>
  );
}

// Shows the admin's mapped Beli system account: when the refresh token
// expires (so operator knows when to re-mitm) and any persistent error.
// Color escalates as expiry approaches: stone (>2d) → amber (≤2d) → red
// (≤24h or already expired or is_dead). Expiry is derived from the JWT
// `exp` claim by the backend; null means no account is mapped.
function SystemAccountStatus({ account }) {
  if (!account) {
    return (
      <div className="bg-white rounded-2xl shadow p-3 text-sm text-red-600">
        No Beli system account mapped to your admin user. Set
        <code className="mx-1 px-1 bg-stone-100 rounded">palate_admin_user_id</code>
        on <code className="px-1 bg-stone-100 rounded">beli_system_account_state</code>.
      </div>
    );
  }
  const expiry = account.refresh_token_expires_at;
  const msLeft = expiry ? new Date(expiry).getTime() - Date.now() : null;
  const dead = account.is_dead;
  const expired = msLeft !== null && msLeft <= 0;
  const urgent = msLeft !== null && msLeft <= 86_400_000; // ≤24h
  const warn = msLeft !== null && msLeft <= 2 * 86_400_000; // ≤2d
  const tone = dead || expired || urgent
    ? 'bg-red-50 border-red-200 text-red-700'
    : warn
      ? 'bg-amber-50 border-amber-200 text-amber-800'
      : 'bg-stone-50 border-stone-200 text-stone-700';

  return (
    <div className={`rounded-2xl border p-3 text-sm ${tone}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <span className="font-medium">Account #{account.account_id}</span>
          {dead && <span className="ml-2 font-semibold">DEAD</span>}
        </div>
        <div className="text-xs">
          Refresh token{' '}
          {expiry
            ? expired
              ? 'expired'
              : `expires ${timeUntil(expiry)}`
            : 'unknown'}
        </div>
      </div>
      {(dead || expired || urgent) && (
        <div className="mt-1 text-xs">
          Re-capture via mitmproxy and reseed{' '}
          <code className="px-1 bg-white/60 rounded">beli_system_account_state</code>.
        </div>
      )}
      {dead && account.last_error && (
        <div className="mt-1 text-xs opacity-80">{account.last_error}</div>
      )}
    </div>
  );
}

function BeliQueueSection() {
  const { session } = useSession();
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [retrying, setRetrying] = useState(null);

  const refresh = useCallback(async () => {
    setError('');
    try {
      const data = await callApi('/api/v1/admin/beli/queue', {
        accessToken: session.access_token,
      });
      setQueue(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [session.access_token]);

  usePolling(refresh, 15000);

  const processQueue = async () => {
    setProcessing(true);
    setError('');
    setLastResult(null);
    try {
      const result = await callApi('/api/v1/admin/beli/process-queue', {
        method: 'POST',
        body: { job_ids: null },
        accessToken: session.access_token,
      });
      setLastResult(result);
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setProcessing(false);
    }
  };

  const retryJob = async (jobId) => {
    setRetrying(jobId);
    setError('');
    try {
      await callApi(`/api/v1/admin/beli/jobs/${jobId}/retry`, {
        method: 'POST',
        accessToken: session.access_token,
      });
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setRetrying(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  const pending = queue?.pending_unclaimed ?? [];
  const claimed = queue?.claimed_by_me ?? [];
  const recent = queue?.recent_completed ?? [];
  const stats = queue?.stats ?? {};

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl shadow p-4 grid grid-cols-3 gap-3 text-center">
        <Stat value={stats.total_pending_unclaimed ?? 0} label="pending" />
        <Stat
          value={stats.last_processed_at ? timeAgo(stats.last_processed_at) : '—'}
          label="last run"
        />
        <Stat
          value={
            stats.avg_processing_seconds_last_10
              ? `${Math.round(stats.avg_processing_seconds_last_10)}s`
              : '—'
          }
          label="avg/job"
        />
      </div>

      <SystemAccountStatus account={stats.system_account} />

      <div className="bg-white rounded-2xl shadow p-4 space-y-3">
        <p className="text-sm text-stone-600">
          Open Beli on your iPhone briefly first to warm the bearer, then click
          Process. Backend will run all pending jobs serially using your account.
        </p>
        <button
          disabled={processing || pending.length === 0}
          onClick={processQueue}
          className="w-full bg-stone-800 text-white rounded-lg py-2 disabled:opacity-50"
        >
          {processing
            ? 'Processing…'
            : pending.length > 0
              ? `Process ${pending.length} pending`
              : 'Nothing to process'}
        </button>
        {lastResult && (
          <p className="text-sm text-stone-700">
            Claimed {lastResult.claimed} job{lastResult.claimed === 1 ? '' : 's'}
            {' '}— processing in the background. Refresh to see progress.
          </p>
        )}
      </div>

      <ErrorBox message={error} />

      <Section title={`Pending (${pending.length})`}>
        {pending.length === 0
          ? <p className="text-sm text-stone-500">Queue is empty.</p>
          : pending.map((j) => (
              <JobRow key={j.job_id} job={j} onRetry={retryJob} retrying={retrying} />
            ))}
      </Section>

      <Section title={`In progress (${claimed.length})`}>
        {claimed.length === 0
          ? <p className="text-sm text-stone-500">None.</p>
          : claimed.map((j) => (
              <JobRow key={j.job_id} job={j} onRetry={retryJob} retrying={retrying} />
            ))}
      </Section>

      <Section title="Recently completed">
        {recent.length === 0
          ? <p className="text-sm text-stone-500">No history yet.</p>
          : recent.map((j) => (
              <JobRow key={j.job_id} job={j} onRetry={retryJob} retrying={retrying} />
            ))}
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Google history (read-only)
// ---------------------------------------------------------------------------
const DAYS_OPTIONS = [
  { value: 7, label: 'last 7 days' },
  { value: 30, label: 'last 30 days' },
  { value: 90, label: 'last 90 days' },
  { value: 3650, label: 'all time' },
];

function GoogleHistorySection() {
  const { session } = useSession();
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      callApi(`/api/v1/admin/google/stats?days=${days}`, {
        accessToken: session.access_token,
      }),
      callApi('/api/v1/admin/google/jobs?limit=50', {
        accessToken: session.access_token,
      }),
    ])
      .then(([s, j]) => {
        setStats(s);
        setItems(j.items);
        setHasMore(j.has_more);
        setCursor(j.next_cursor);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [days, session.access_token]);

  const loadMore = async () => {
    try {
      const d = await callApi(
        `/api/v1/admin/google/jobs?limit=50&cursor=${encodeURIComponent(cursor || '')}`,
        { accessToken: session.access_token }
      );
      setItems((p) => [...p, ...d.items]);
      setHasMore(d.has_more);
      setCursor(d.next_cursor);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value, 10))}
          className="text-sm border rounded px-2 py-1"
        >
          {DAYS_OPTIONS.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
      </div>

      <Section title="Stats">
        {stats ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat value={stats.jobs_run} label="jobs" />
            <Stat value={stats.items_added_total} label="added" />
            <Stat value={stats.items_unmatched_non_nyc_total} label="non-NYC" />
            <Stat value={stats.truncated_count} label="truncated" />
          </div>
        ) : <LoadingSpinner />}
      </Section>

      <ErrorBox message={error} />

      <Section title="Recent imports">
        {loading ? <LoadingSpinner /> : (
          items.length === 0 ? <EmptyState>No Google imports yet.</EmptyState> : (
            <div className="divide-y">
              {items.map((j) => (
                <div key={j.id} className="py-2">
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <UserChip userId={j.palate_user_id} displayName={j.display_name} />
                      <div className="text-xs text-stone-500 truncate mt-0.5">
                        {j.list_name_from_google || j.share_url}
                      </div>
                      <div className="text-xs text-stone-600 mt-0.5">
                        {j.items_added} added · {j.items_already_present} already in ·{' '}
                        {j.items_unmatched_non_nyc} non-NYC · {j.items_unmatched_nyc} no-match
                        {j.truncated && ' · truncated'}
                      </div>
                    </div>
                    <div className="text-xs text-stone-500 whitespace-nowrap">
                      {timeAgo(j.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
        {hasMore && (
          <button
            onClick={loadMore}
            className="text-sm text-stone-700 underline mt-2"
          >
            Load more
          </button>
        )}
      </Section>
    </div>
  );
}
